import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Barbell } from "@phosphor-icons/react";
import api, { extractError } from "@/lib/api";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const emptySet = () => ({ id: uid(), reps: 8, weight: 60 });
const emptyExercise = () => ({ id: uid(), name: "", sets: [emptySet()], notes: "" });

export default function LogWorkout() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(60);
  const [exercises, setExercises] = useState([emptyExercise()]);
  const [saving, setSaving] = useState(false);

  const updateExercise = (i, patch) => {
    setExercises((prev) => prev.map((ex, idx) => (idx === i ? { ...ex, ...patch } : ex)));
  };
  const updateSet = (exI, setI, patch) => {
    setExercises((prev) =>
      prev.map((ex, i) =>
        i === exI ? { ...ex, sets: ex.sets.map((s, si) => (si === setI ? { ...s, ...patch } : s)) } : ex
      )
    );
  };
  const addSet = (i) => updateExercise(i, { sets: [...exercises[i].sets, emptySet()] });
  const removeSet = (exI, setI) =>
    updateExercise(exI, { sets: exercises[exI].sets.filter((_, si) => si !== setI) });
  const addExercise = () => setExercises((prev) => [...prev, emptyExercise()]);
  const removeExercise = (i) => setExercises((prev) => prev.filter((_, idx) => idx !== i));

  const totalVolume = exercises.reduce(
    (sum, ex) => sum + ex.sets.reduce((s, st) => s + (Number(st.reps) || 0) * (Number(st.weight) || 0), 0),
    0
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    const cleaned = exercises
      .map((ex) => ({ ...ex, name: ex.name.trim(), sets: ex.sets.filter((s) => s.reps > 0 || s.weight > 0) }))
      .filter((ex) => ex.name && ex.sets.length > 0);
    if (!title.trim()) return toast.error("Add a title");
    if (cleaned.length === 0) return toast.error("Add at least one exercise with sets");

    setSaving(true);
    try {
      await api.post("/workouts", {
        title: title.trim(),
        description: description.trim(),
        duration_min: Number(duration) || 0,
        exercises: cleaned.map((ex) => ({
          name: ex.name,
          notes: ex.notes || "",
          sets: ex.sets.map((s) => ({ reps: Number(s.reps) || 0, weight: Number(s.weight) || 0 })),
        })),
      });
      toast.success("Workout logged");
      navigate("/feed");
    } catch (err) {
      toast.error(extractError(err, "Failed to log workout"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-8" data-testid="log-workout-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono">// Log</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase">New session</h1>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total volume</div>
          <div className="font-display text-3xl font-black text-[#FF3B30]" data-testid="log-total-volume">
            {Math.round(totalVolume).toLocaleString()}<span className="text-sm text-zinc-500 font-mono ml-1">kg</span>
          </div>
        </div>
      </div>

      {/* Meta */}
      <div className="border border-[#27272A] bg-[#121212] p-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Title</label>
          <input data-testid="log-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Push Day, Heavy Squats..." className="input-line" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Notes</label>
          <input data-testid="log-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="How did it feel?" className="input-line" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold block mb-1">Duration (min)</label>
          <input data-testid="log-duration-input" type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="input-line" />
        </div>
      </div>

      {/* Exercises */}
      <div className="space-y-4">
        {exercises.map((ex, i) => (
          <div key={ex.id} data-testid={`exercise-block-${i}`} className="border border-[#27272A] bg-[#121212] p-5">
            <div className="flex items-center gap-3 mb-4">
              <Barbell size={20} weight="bold" className="text-[#FF3B30]" />
              <input
                data-testid={`exercise-name-${i}`}
                required
                placeholder="Exercise name (e.g. Barbell Squat)"
                value={ex.name}
                onChange={(e) => updateExercise(i, { name: e.target.value })}
                className="input-line font-display uppercase text-lg font-bold"
              />
              {exercises.length > 1 && (
                <button type="button" onClick={() => removeExercise(i)} className="text-zinc-500 hover:text-[#FF3B30] p-1" data-testid={`remove-exercise-${i}`}>
                  <Trash size={18} weight="bold" />
                </button>
              )}
            </div>

            {/* Sets */}
            <div className="space-y-2">
              <div className="grid grid-cols-[40px_1fr_1fr_40px] gap-2 items-center text-[10px] uppercase tracking-widest text-zinc-500 font-semibold pb-1 border-b border-[#27272A]">
                <span>Set</span>
                <span>Reps</span>
                <span>Weight (kg)</span>
                <span></span>
              </div>
              {ex.sets.map((s, si) => (
                <div key={s.id} className="grid grid-cols-[40px_1fr_1fr_40px] gap-2 items-center">
                  <span className="font-mono text-zinc-500 text-sm">{si + 1}</span>
                  <input
                    data-testid={`ex-${i}-set-${si}-reps`}
                    type="number" min="0"
                    value={s.reps}
                    onChange={(e) => updateSet(i, si, { reps: e.target.value })}
                    className="bg-[#1E1E1E] border border-[#27272A] focus:border-[#FF3B30] outline-none px-3 py-2 text-white font-mono text-sm"
                  />
                  <input
                    data-testid={`ex-${i}-set-${si}-weight`}
                    type="number" min="0" step="0.5"
                    value={s.weight}
                    onChange={(e) => updateSet(i, si, { weight: e.target.value })}
                    className="bg-[#1E1E1E] border border-[#27272A] focus:border-[#FF3B30] outline-none px-3 py-2 text-white font-mono text-sm"
                  />
                  {ex.sets.length > 1 ? (
                    <button type="button" onClick={() => removeSet(i, si)} className="text-zinc-600 hover:text-[#FF3B30]" data-testid={`remove-set-${i}-${si}`}>
                      <Trash size={16} />
                    </button>
                  ) : <span />}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => addSet(i)}
              data-testid={`add-set-${i}`}
              className="mt-3 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] font-semibold flex items-center gap-1"
            >
              <Plus size={14} weight="bold" /> Add set
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addExercise}
          data-testid="add-exercise-button"
          className="w-full border border-dashed border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-500 py-6 flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-bold transition-colors"
        >
          <Plus size={16} weight="bold" /> Add exercise
        </button>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={() => navigate("/feed")} className="btn-ghost">Cancel</button>
        <button data-testid="save-workout-button" type="submit" disabled={saving} className="btn-primary">
          {saving ? "Saving..." : "Save workout"}
        </button>
      </div>
    </form>
  );
}
